import { useState, useEffect, useContext, useCallback, useMemo } from 'react';

import * as THREE from 'three';

import { DesignContext } from '../contexts/designContext.js';

import {
  pitchMap,
  isClockWise,
  dist,
  counterClockWise,
  calculateRoll,
  expandPolygon,
  expandPolygonWithRoll,
  calculatePolygonZdim,
  pointInConvexPolygon,
  pointInConcavePolygon,
  polygonContainsPolygon,
  calculateRoofHeight,
  optimizeVertexPosition,
  getEaveIndices,
  getEave,
  shrinkPolygon,
  getParentsPolygon,
  segmentProperties,
  projectToLine,
  protectMethod,
  getPlaneAxisForPanels,
  getEaveFromPanelAxis,
  calculatePanelAxis,
  mapPanelsTo3D,
  polygonsIntersect
} from '../algorithms/geometry.js';

import {
  initializePanel,
  getPanelData,
  getSetbackData,
  runPanelPlacement,
  getRoofTransformationInfo
} from '../algorithms/panelOperation.js';

let panelDataOldData = null;
const interactivePlacementLimits = {
  maxPanelsPerRoof: 256,
  maxTestCellsPerRoof: 20000,
  compactGridSteps: 5
};

export const usePanelOperation = ({
  segments,
  panels,
  vertices,
  vertices3D,
  orgVertices,
  verticesType,
  parents,
  eaves,
  pitches,
  azimuths,
  eaveHeights,
  changeClockwise,
  inchePerUnit,
  scale,
  center,
  getSetbacks,
  readonly,
  designerCapabilities
}) => {
  const {
    resolution,
    panelType,
    buffer,
    rowSpacing,
    panelTilt,
    azimuthRestriction,
    setback,
    selectedObjects,
    setPanelPlacement,
    panelPlacementFillMode,
    panelPlacementLayoutMode,
    panelPlacementOnlySelectedRoof
  } = useContext(DesignContext);

  const [cx, cy] = center;
  const c = 1;

  const [panelData, setPanelData] = useState([]);
  const [moveStatus, setMoveStatus] = useState(0);

  const initPanel = (i) => initializePanel(segments[i], pitches[i], panelTilt, panelType, resolution);
  const initPanels = () => segments.map((s, si) => initPanel(si));

  const getPanelAxisLines = useCallback(
    (si) => {
      const lines = segments?.[si]?.lines;
      if (!Array.isArray(lines)) return null;
      return changeClockwise?.[si] ? [...lines].reverse() : lines;
    },
    [segments, changeClockwise]
  );

  const serializePanelAxis = useCallback((axis) => {
    const [, , eaveType, ai, bi] = axis ?? [];
    const edgeIndex = Number(ai);
    const nextEdgeIndex = Number(bi);
    if (!Number.isInteger(edgeIndex) || !Number.isInteger(nextEdgeIndex)) return null;
    return [eaveType ?? '', edgeIndex, nextEdgeIndex];
  }, []);

  const persistPanelAxes = useCallback(
    (segmentIndices, basePanelEaves = []) => {
      const nextPanelEaves = [...basePanelEaves];
      const panelAxes = {};
      segmentIndices.forEach((si) => {
        const axis = calculatePanelAxis(orgVertices?.[si], segments?.[si]?.azimuth, getPanelAxisLines(si));
        const panelAxis = serializePanelAxis(axis);
        if (!panelAxis || !segments?.[si]) return;
        panelAxes[si] = panelAxis;
        nextPanelEaves[si] = getEaveFromPanelAxis(orgVertices?.[si], panelAxis) ?? axis;
      });
      return { panelEaves: nextPanelEaves, panelAxes };
    },
    [getPanelAxisLines, orgVertices, segments, serializePanelAxis]
  );

  const panelEaves = useMemo(
    () =>
      (orgVertices ?? []).map((poly, si) => {
        const storedPanelAxis = segments?.[si]?.panelAxis ?? panels?.[si]?.panelAxis;
        const storedAxis = getEaveFromPanelAxis(poly, storedPanelAxis);
        const fallbackEave = eaves?.[si] ?? null;
        return storedAxis ?? fallbackEave;
      }),
    [orgVertices, segments, panels, eaves]
  );

  useEffect(() => {
    if (!Array.isArray(segments) || segments.length === 0) return;
    if (readonly) return;
    const hasPanelsArray = Array.isArray(panels) && panels.length === segments.length;
    if (hasPanelsArray) return;
    if (Array.isArray(panels) && panels.length > 0) return;
    setPanelPlacement(initPanels());
  }, [segments, pitches, panelTilt, panelType, resolution, readonly, panels, setPanelPlacement]);

  const clearPanels = (onlySelectedRoof = false) => {
    if (onlySelectedRoof) {
      const sr = selectedObjects.roofSegments || segments.map((seg, index) => index);
      const emptyPanels = initPanels();
      const newPanels = panels.map((p, pi) => (sr.includes(pi) ? emptyPanels[pi] : p));
      setPanelPlacement(newPanels);
    } else {
      const newPanels = initPanels();
      setPanelPlacement(newPanels);
    }
  };

  const panelPlacement = useCallback(
    (all = false, fillMode = 3, layoutMode = 1) => {
      const hasRoofSelection = (selectedObjects.roofSegments?.length ?? 0) > 0;
      const restrictToSelection = panelPlacementOnlySelectedRoof;
      all = all || !restrictToSelection || !hasRoofSelection;
      const segmentIndices = all ? segments.map((_, si) => si) : selectedObjects.roofSegments;
      const { panelEaves: nextPanelEaves, panelAxes } = persistPanelAxes(segmentIndices, panelEaves);
      const newPanels = runPanelPlacement(
        {
          orgVertices,
          vertices3D,
          verticesType,
          parents,
          eaves: nextPanelEaves,
          pitches,
          azimuths,
          center
        },
        {
          segments,
          panels,
          panelType,
          scale,
          resolution,
          buffer,
          rowSpacing,
          panelTilt,
          azimuthRestriction,
          ...interactivePlacementLimits
        },
        getSetbacks,
        all,
        selectedObjects.roofSegments,
        fillMode,
        layoutMode
      );
      Object.entries(panelAxes).forEach(([si, panelAxis]) => {
        if (newPanels?.[si]) newPanels[si].panelAxis = panelAxis;
      });
      setPanelPlacement(newPanels);
    },
    [
      buffer,
      rowSpacing,
      panelTilt,
      azimuthRestriction,
      cx,
      cy,
      resolution,
      scale,
      segments,
      panels,
      panelEaves,
      persistPanelAxes,
      readonly,
      panelType,
      panelPlacementOnlySelectedRoof,
      selectedObjects.roofSegments,
      verticesType,
      parents
    ]
  );

  const panelPlacementByIndices = useCallback(
    (segmentIndices, fillMode = 3, layoutMode = 1) => {
      const { panelEaves: nextPanelEaves, panelAxes } = persistPanelAxes(segmentIndices, panelEaves);
      const newPanels = runPanelPlacement(
        {
          orgVertices,
          vertices3D,
          verticesType,
          parents,
          eaves: nextPanelEaves,
          pitches,
          azimuths,
          center
        },
        {
          segments,
          panels,
          panelType,
          scale,
          resolution,
          buffer,
          rowSpacing,
          panelTilt,
          azimuthRestriction,
          ...interactivePlacementLimits
        },
        getSetbacks,
        false,
        segmentIndices,
        fillMode,
        layoutMode
      );
      Object.entries(panelAxes).forEach(([si, panelAxis]) => {
        if (newPanels?.[si]) newPanels[si].panelAxis = panelAxis;
      });
      setPanelPlacement(newPanels);
    },
    [
      buffer,
      rowSpacing,
      panelTilt,
      azimuthRestriction,
      cx,
      cy,
      resolution,
      scale,
      segments,
      panels,
      panelEaves,
      persistPanelAxes,
      readonly,
      panelType,
      selectedObjects,
      verticesType,
      parents
    ]
  );

  const deletePanels = useCallback(() => {
    if (readonly) return;
    const newPanels = [...panels];
    const spnls = selectedObjects.panels;

    Object.keys(spnls).forEach((pi) => {
      const npi = parseInt(pi, 10);
      spnls[npi].sort((fEl, sEl) => parseInt(sEl, 10) - parseInt(fEl, 10));
      spnls[npi].forEach((pli) => {
        const hlen = newPanels[npi].horizontal.points.length;
        if (pli < hlen) newPanels[npi].horizontal.points.splice(pli, 1);
        else newPanels[npi].vertical.points.splice(pli - hlen, 1);
      });
    });

    newPanels.forEach((pnls) => {
      pnls.horizontal.selected = new Array(pnls.horizontal.points.length).fill(false);
      pnls.vertical.selected = new Array(pnls.vertical.points.length).fill(false);
    });

    setPanelPlacement(newPanels);
    selectedObjects.panels = [];
  }, [panels, readonly, selectedObjects.panels, setPanelPlacement]);

  const rotatePanels = useCallback(() => {
    if (readonly) return;
    const newPanels = [...panels];
    const spnls = selectedObjects.panels;
    const newSpnls = {};

    Object.keys(spnls).forEach((pi) => {
      const npi = parseInt(pi, 10);
      if (spnls[npi].length) {
        spnls[npi].sort((fEl, sEl) => parseInt(sEl, 10) - parseInt(fEl, 10));
        newSpnls[npi] = [];
        const hlen = newPanels[npi].horizontal.points.length;
        const vlen = newPanels[npi].vertical.points.length;
        const nsH = spnls[npi].filter((s) => s < hlen).length;
        const nsV = spnls[npi].length - nsH;
        const newH = hlen - nsH + nsV;
        const newV = vlen - nsV + nsH;
        Array(nsH)
          .fill(0)
          .forEach((_, ni) => newSpnls[npi].push(newH + newV - ni - 1));
        Array(nsV)
          .fill(0)
          .forEach((n, ni) => newSpnls[npi].push(newH - ni - 1));
        spnls[npi].forEach((pli) => {
          if (pli < hlen) {
            const pnl = newPanels[npi].horizontal.points.splice(pli, 1);
            newPanels[npi].vertical.points.push(pnl[0]);
          } else {
            const pnl = newPanels[npi].vertical.points.splice(pli - hlen, 1);
            newPanels[npi].horizontal.points.push(pnl[0]);
          }
        });
      }
    });

    setPanelPlacement(newPanels);
    selectedObjects.panels = newSpnls;
  }, [panels, readonly, selectedObjects.panels, setPanelPlacement]);

  const findNearestPoint = (testPoint, points) => {
    let minDist = Infinity;
    let minIndex = -1;
    for (let i = 0; i < points.length; i++) {
      const dist = testPoint.distanceTo(points[i]);
      if (dist < minDist) {
        minDist = dist;
        minIndex = i;
      }
    }
    return {
      index: minIndex,
      distance: minDist
    };
  };

  const findNearestBetweenLists = (listA, listB) => {
    let globalMinDist = Infinity;
    let bestA = -1;
    let bestB = -1;

    for (let i = 0; i < listA.length; i++) {
      const { index: j, distance } = findNearestPoint(listA[i], listB);
      if (distance < globalMinDist) {
        globalMinDist = distance;
        bestA = i;
        bestB = j;
      }
    }

    return {
      indexA: bestA,
      indexB: bestB,
      distance: globalMinDist
    };
  };

  const getInsertPanelPosition = useCallback(
    (pi, position, type = '', panelState = panels) => {
      const finalType =
        type === 'v' || (type == '' && (panelPlacementFillMode == 2 || panelPlacementFillMode == 3)) ? 'v' : 'h';
      let pos = [position.x / scale + cx / c, position.z / scale + cy / c];
      const isLegacy = !!segments?.[pi]?.is_lagacy;
      if (isLegacy && panelEaves?.[pi] && vertices3D?.[pi]) {
        const azimuthShift = (segments?.[pi]?.azimuth ?? 0) - (panelState?.[pi]?.azimuth ?? 0);
        const [xdir, ydir] = getPlaneAxisForPanels(panelEaves[pi], vertices3D[pi], azimuthShift);
        if (xdir && ydir) {
          const vw = finalType === 'v' ? panelState[pi].vertical.width : panelState[pi].horizontal.width;
          const vh = finalType === 'v' ? panelState[pi].vertical.height : panelState[pi].horizontal.height;
          pos = [pos[0] - xdir.x * vw + ydir.x * vh, pos[1] - xdir.y * vw + ydir.y * vh];
        }
      }
      return [pos, finalType];
    },
    [cx, cy, panels, panelEaves, panelPlacementFillMode, scale, segments, vertices3D]
  );

  const getPanelRectForInsert = useCallback(
    (pi, pos, type, panelState = panels) => {
      if (!panelEaves?.[pi] || !vertices3D?.[pi] || !panelState?.[pi]) return null;
      const panel = panelState[pi];
      const panelTypeData = type === 'v' ? panel.vertical : panel.horizontal;
      const azimuthShift = panelData?.[pi]?.azimuthShift ?? (segments?.[pi]?.azimuth ?? 0) - (panel?.azimuth ?? 0);
      const point = [
        (pos[0] - cx / c) * scale,
        (pos[1] - cy / c) * scale,
        panelTypeData.height * scale,
        panelTypeData.width * scale,
        type
      ];
      if (segments?.[pi]?.is_lagacy) {
        const [xdir, ydir] = getPlaneAxisForPanels(panelEaves[pi], vertices3D[pi], azimuthShift);
        if (xdir && ydir) {
          point[0] += xdir.x * point[3] - ydir.x * point[2];
          point[1] += xdir.y * point[3] - ydir.y * point[2];
        }
      }
      const rects = mapPanelsTo3D(panelEaves[pi], vertices3D[pi], [point], azimuthShift);
      return rects?.[0] ?? null;
    },
    [c, cx, cy, panelData, panelEaves, panels, scale, segments, vertices3D]
  );

  const canInsertPanel = useCallback(
    (pi, position, type = '', panelStateOverride = null) => {
      if (readonly) return false;
      if (!Number.isInteger(Number(pi))) return false;
      const npi = parseInt(pi, 10);
      const placedPanelCount = Array.isArray(panels)
        ? panels.reduce(
            (total, panel) => total + (panel?.horizontal?.points?.length || 0) + (panel?.vertical?.points?.length || 0),
            0
          )
        : 0;
      const panelState =
        panelStateOverride ??
        (!Array.isArray(panels) || panels.length === 0 || placedPanelCount === 0 ? initPanels() : panels);
      if (!panelState?.[npi] || !panelEaves?.[npi] || !vertices3D?.[npi]) return false;
      const [pos, finalType] = getInsertPanelPosition(npi, position, type, panelState);
      const rect = getPanelRectForInsert(npi, pos, finalType, panelState);
      if (!rect) return false;
      if (!polygonContainsPolygon(vertices3D[npi], rect, 0.001)) return false;
      const realRects = panelData?.[npi]?.points3D?.filter((_, idx) => panelData?.[npi]?.realPanels?.[idx] !== false) ?? [];
      return realRects.every((existing) => !polygonsIntersect(existing, rect, false, 0.001));
    },
    [getInsertPanelPosition, getPanelRectForInsert, initPanels, panelData, panelEaves, panels, readonly, vertices3D]
  );

  const getMovePreviewStatus = useCallback(
    (oldPosition, newPosition) => {
      if (!panelDataOldData) return { valid: true, overlaps: false };
      const offset = new THREE.Vector3(
        newPosition.x - oldPosition.x,
        newPosition.z - oldPosition.z,
        newPosition.y - oldPosition.y
      );
      const selectedRects = [];
      const selectedKeys = new Set();
      Object.keys(selectedObjects.panels).forEach((pi) => {
        const npi = parseInt(pi, 10);
        const roof = vertices3D?.[npi];
        const roofPanelData = panelData?.[npi];
        if (!roof || !roofPanelData?.points3D) return;
        selectedObjects.panels[pi].forEach((pli) => {
          const rect = roofPanelData.points3D[pli];
          if (!Array.isArray(rect)) return;
          selectedKeys.add(`${npi}-${pli}`);
          const movedRect = rect.map((p) => p.clone().add(offset));
          selectedRects.push({ roofIndex: npi, panelIndex: pli, rect: movedRect, roof });
        });
      });
      const valid = selectedRects.every(({ rect, roof }) => {
        return polygonContainsPolygon(roof, rect, 0.001);
      });
      if (!valid) return { valid: false, overlaps: false };
      const otherRects = [];
      panelData.forEach((roofPanelData, roofIndex) => {
        roofPanelData?.points3D?.forEach((rect, panelIndex) => {
          if (!Array.isArray(rect)) return;
          if (roofPanelData?.realPanels?.[panelIndex] === false) return;
          if (selectedKeys.has(`${roofIndex}-${panelIndex}`)) return;
          otherRects.push(rect);
        });
      });
      const overlaps = selectedRects.some(({ rect }) =>
        otherRects.some((otherRect) => polygonsIntersect(rect, otherRect, false, 0.001))
      );
      return { valid: true, overlaps };
    },
    [panelData, selectedObjects.panels, vertices3D]
  );

  const areSelectedPanelPositionsValid = useCallback(
    (panelState) => {
      const selectedKeys = new Set();
      const selectedRects = [];
      const validRoof = Object.keys(selectedObjects.panels).every((pi) => {
        const npi = parseInt(pi, 10);
        const roof = vertices3D?.[npi];
        const panel = panelState?.[npi];
        if (!roof || !panelEaves?.[npi] || !panel) return true;
        const hlen = panel.horizontal.points.length;
        const azimuthShift = panelData?.[npi]?.azimuthShift ?? (segments?.[npi]?.azimuth ?? 0) - (panel?.azimuth ?? 0);
        return selectedObjects.panels[pi].every((pli) => {
          const isHorizontal = pli < hlen;
          const point = isHorizontal ? panel.horizontal.points[pli] : panel.vertical.points[pli - hlen];
          const shape = isHorizontal ? panel.horizontal : panel.vertical;
          if (!point || !shape) return true;
          const mappedPoint = [
            (point[0] - cx / c) * scale,
            (point[1] - cy / c) * scale,
            shape.height * scale,
            shape.width * scale,
            isHorizontal ? 'h' : 'v'
          ];
          if (segments?.[npi]?.is_lagacy) {
            const [xdir, ydir] = getPlaneAxisForPanels(panelEaves[npi], roof, azimuthShift);
            if (xdir && ydir) {
              mappedPoint[0] += xdir.x * mappedPoint[3] - ydir.x * mappedPoint[2];
              mappedPoint[1] += xdir.y * mappedPoint[3] - ydir.y * mappedPoint[2];
            }
          }
          const rect = mapPanelsTo3D(panelEaves[npi], roof, [mappedPoint], azimuthShift)?.[0];
          if (!rect) return true;
          selectedKeys.add(`${npi}-${pli}`);
          selectedRects.push({ rect, roofIndex: npi, panelIndex: pli });
          return polygonContainsPolygon(roof, rect, 0.001);
        });
      });
      if (!validRoof) return false;
      const otherRects = [];
      panelData.forEach((roofPanelData, roofIndex) => {
        roofPanelData?.points3D?.forEach((rect, panelIndex) => {
          if (!Array.isArray(rect)) return;
          if (selectedKeys.has(`${roofIndex}-${panelIndex}`)) return;
          if (roofPanelData?.realPanels?.[panelIndex] === false) return;
          otherRects.push(rect);
        });
      });
      return selectedRects.every(({ rect }) =>
        otherRects.every((otherRect) => !polygonsIntersect(rect, otherRect, false, 0.001))
      );
    },
    [c, cx, cy, panelData, panelEaves, scale, selectedObjects.panels, segments, vertices3D]
  );

  const movePanels = useCallback(
    (oldPosition, newPosition, status) => {
      if (readonly) return;
      const DIST_THRESHOLD = 2.5;
      if (status === 1) {
        panelDataOldData = JSON.parse(JSON.stringify(panels));
        setMoveStatus(status);
        return true;
      } else if (status === 2) {
        const previewStatus = getMovePreviewStatus(oldPosition, newPosition);
        setMoveStatus(status);
        return {
          isPanelMoveResult: true,
          position: newPosition,
          invalid: !previewStatus.valid || previewStatus.overlaps
        };
      } else if (status === 3) {
        let [ofx, ofy] = [(newPosition.x - oldPosition.x) / scale, (newPosition.z - oldPosition.z) / scale];
        const v = newPosition.clone().sub(oldPosition);
        v.set(v.x, v.z, v.y);
        const newPanels = JSON.parse(JSON.stringify(panels));
        const z = new THREE.Vector3(0, 0, 1);
        const is_snap = designerCapabilities?.includes('move-panel-snap');
        let [ox, oy] = [0, 0];
        Object.keys(selectedObjects.panels).forEach((pi) => {
          const hasEavex = panelEaves[pi] && panelEaves[pi][1];
          const [eaveDirection, eavePerpDirection, n] = getPlaneAxisForPanels(panelEaves[pi], vertices3D[pi]);
          if (!eaveDirection || !eavePerpDirection) return;
          // const eaveDirection = hasEavex ? eaves[pi][1].clone().sub(eaves[pi][0]).normalize() : new THREE.Vector3(1, 0, 0);
          // const eavePerpDirection = eaveDirection.clone().cross(z).normalize();
          if (is_snap) {
            const rackBuffer = Math.max(0.001, buffer / 10 / Number(resolution));
            let minDistance = Number.MAX_VALUE;
            const snap_points = panelData[pi].snap_points.slice();

            const indices = selectedObjects.panels[pi];
            const indicesSet = new Set(indices);
            const panel_box_points = indices.map((i) => snap_points[i]).flat();
            const remaining_snap_points = snap_points.filter((_, idx) => !indicesSet.has(idx)).flat();
            if (remaining_snap_points.length > 0) {
              const translated_points = panel_box_points.map((point) => point.clone().add(v));
              const np = findNearestBetweenLists(translated_points, remaining_snap_points);
              if (np.distance < DIST_THRESHOLD && np.distance < minDistance) {
                minDistance = np.distance;
                const boxPoint = translated_points[np.indexA];
                const snapPoint = remaining_snap_points[np.indexB];
                const offset = snapPoint.clone().sub(boxPoint);
                offset.set(offset.x, offset.z, offset.y);
                const correctedPosition = newPosition.clone().add(offset);
                [ofx, ofy] = [(correctedPosition.x - oldPosition.x) / scale, (correctedPosition.z - oldPosition.z) / scale];
                ox = oy = 0;
                const ia = np.indexA % 4;
                const ib = np.indexB % 4;
                if (Math.abs(ia - ib) == 2) {
                  ox = oy = rackBuffer;
                  if (ib == 1 || ib == 2) ox = -ox;
                  if (ib == 2 || ib == 3) oy = -oy;
                } else if ((ia + ib) % 4 == 1) {
                  if (ia == 1 || ia == 2) ox = rackBuffer;
                  else ox = -rackBuffer;
                } else {
                  if (ia == 2 || ia == 3) oy = rackBuffer;
                  else oy = -rackBuffer;
                }
              }
            }
          }
          selectedObjects.panels[pi].forEach((pli) => {
            const npi = parseInt(pi, 10);
            const hlen = newPanels[pi].horizontal.points.length;
            if (pli < hlen && newPanels[npi].horizontal.points && panelDataOldData[npi].horizontal.points) {
              newPanels[npi].horizontal.points[pli][0] =
                panelDataOldData[npi].horizontal.points[pli][0] + ofx + eaveDirection.x * ox - eavePerpDirection.x * oy;
              newPanels[npi].horizontal.points[pli][1] =
                panelDataOldData[npi].horizontal.points[pli][1] + ofy + eaveDirection.y * ox - eavePerpDirection.y * oy;
            } else if (newPanels[npi].vertical.points && panelDataOldData[npi].vertical.points) {
              newPanels[npi].vertical.points[pli - hlen][0] =
                panelDataOldData[npi].vertical.points[pli - hlen][0] + ofx + eaveDirection.x * ox - eavePerpDirection.x * oy;
              newPanels[npi].vertical.points[pli - hlen][1] =
                panelDataOldData[npi].vertical.points[pli - hlen][1] + ofy + eaveDirection.y * ox - eavePerpDirection.y * oy;
            }
          });
        });
        if (!areSelectedPanelPositionsValid(newPanels)) {
          setMoveStatus(0);
          return false;
        }
        setPanelPlacement(newPanels);
        setMoveStatus(0);
        return true;
      }
      setMoveStatus(status);
      return true;
    },
    [
      panelEaves,
      vertices3D,
      panels,
      readonly,
      scale,
      selectedObjects.panels,
      setPanelPlacement,
      panelData,
      designerCapabilities,
      setMoveStatus,
      areSelectedPanelPositionsValid,
      getMovePreviewStatus
    ]
  );

  const insertPanels = useCallback(
    (pi, position, type = '') => {
      if (readonly) return;
      const placedPanelCount = Array.isArray(panels)
        ? panels.reduce(
            (total, panel) => total + (panel?.horizontal?.points?.length || 0) + (panel?.vertical?.points?.length || 0),
            0
          )
        : 0;
      let newPanels = !Array.isArray(panels) || panels.length === 0 || placedPanelCount === 0 ? initPanels() : [...panels];
      const segmentPanelCount =
        (panels?.[pi]?.horizontal?.points?.length || 0) + (panels?.[pi]?.vertical?.points?.length || 0);
      if (segmentPanelCount === 0) {
        if (!panelEaves?.[pi] || !vertices3D?.[pi]) return;
        const [xdir, ydir] = getPlaneAxisForPanels(panelEaves[pi], vertices3D[pi]);
        if (!xdir || !ydir) return;
        const { panelAxes } = persistPanelAxes([pi], panelEaves);
        if (newPanels?.[pi] && panelAxes?.[pi]) newPanels[pi].panelAxis = panelAxes[pi];
      }
      if (!canInsertPanel(pi, position, type, newPanels)) return;
      const [pos, finalType] = getInsertPanelPosition(pi, position, type, newPanels);

      if (finalType === 'v') newPanels[pi].vertical.points.push(pos);
      else newPanels[pi].horizontal.points.push(pos);
      setPanelPlacement(newPanels);
    },
    [
      cx,
      cy,
      canInsertPanel,
      getInsertPanelPosition,
      panels,
      readonly,
      scale,
      panelPlacementFillMode,
      panelType,
      panelTilt,
      resolution,
      segments,
      panelEaves,
      persistPanelAxes,
      orgVertices,
      vertices3D,
      setPanelPlacement
    ]
  );

  const alignHorizontal = useCallback(() => {
    if (readonly) return;
    const rackBuffer = Math.max(0.001, buffer / 10 / Number(resolution));
    const newPanels = [...panels];
    Object.keys(selectedObjects.panels).forEach((pi) => {
      const row = [];
      const [AB, ,] = getPlaneAxisForPanels(panelEaves[pi], vertices3D[pi], panelData[pi].azimuthShift);
      if (!AB) return;
      const [, , , ai] = panelEaves[pi];
      const a = vertices3D[pi][ai];
      let max_d = Number.MIN_VALUE;
      selectedObjects.panels[pi].forEach((vi) => {
        const p = panelData[pi].points3D[vi][0];
        const AP = p.clone().sub(a);
        // A + dot(AP,AB) / dot(AB,AB) * AB
        const d = Math.round(AP.dot(AB) / AB.dot(AB), 4);
        if (d > max_d) max_d = d;
        row.push([vi, d]);
      });
      if (row.length) {
        const hlen = newPanels[pi].horizontal.points.length;
        const [x, y] =
          row[0][0] >= hlen ? newPanels[pi].vertical.points[row[0][0] - hlen] : newPanels[pi].horizontal.points[row[0][0]];
        const d0 = row[0][1];
        const rowx = row.map((e) => [e[0], e[1] - d0, e[1] - d0 >= 0 ? e[1] - d0 : d0 - e[1] + max_d + 1]);
        rowx.sort((e1, e2) => e1[2] - e2[2]);
        const xw = new THREE.Vector3(0, 0, 0);
        let changeInDirection = false;
        rowx.forEach(([vi, d], i) => {
          if (d < 0 && !changeInDirection) {
            const w = rowx[0][0] >= hlen ? newPanels[pi].vertical.width : newPanels[pi].horizontal.width;
            changeInDirection = true;
            xw.set(0, 0, 0);
            xw.sub(AB.clone().multiplyScalar(w + rackBuffer));
          } else if (d < 0) {
            const w = rowx[i - 1][0] >= hlen ? newPanels[pi].vertical.width : newPanels[pi].horizontal.width;
            xw.sub(AB.clone().multiplyScalar(w + rackBuffer));
          } else {
            const w = vi >= hlen ? newPanels[pi].vertical.width : newPanels[pi].horizontal.width;
            xw.add(AB.clone().multiplyScalar((w + rackBuffer) * (i == 0 ? 0 : 1)));
          }
          if (vi >= hlen) {
            newPanels[pi].vertical.points[vi - hlen] = [x + xw.x, y + xw.y];
          } else {
            newPanels[pi].horizontal.points[vi] = [x + xw.x, y + xw.y];
          }
        });
      }
    });
    setPanelPlacement(newPanels);
  }, [panelEaves, panelData, panels, readonly, selectedObjects.panels, setPanelPlacement]);

  const alignVertical = useCallback(() => {
    if (readonly) return;
    const rackBuffer = Math.max(0.001, buffer / 10 / Number(resolution));
    const newPanels = [...panels];
    Object.keys(selectedObjects.panels).forEach((pi) => {
      const row = [];
      const [, AB] = getPlaneAxisForPanels(panelEaves[pi], vertices3D[pi], panelData[pi].azimuthShift);
      if (!AB) return;
      const [, , , ai] = panelEaves[pi];
      const a = vertices3D[pi][ai];
      let max_d = Number.MIN_VALUE;
      selectedObjects.panels[pi].forEach((vi) => {
        const p = panelData[pi].points3D[vi][0];
        const AP = p.clone().sub(a);
        // A + dot(AP,AB) / dot(AB,AB) * AB
        const d = Math.round(AP.dot(AB) / AB.dot(AB), 4);
        if (d > max_d) max_d = d;
        row.push([vi, d]);
      });
      if (row.length) {
        const hlen = newPanels[pi].horizontal.points.length;
        const [x, y] =
          row[0][0] >= hlen ? newPanels[pi].vertical.points[row[0][0] - hlen] : newPanels[pi].horizontal.points[row[0][0]];
        const d0 = row[0][1];
        const rowx = row.map((e) => [e[0], e[1] - d0, e[1] - d0 >= 0 ? e[1] - d0 : d0 - e[1] + max_d + 1]);
        rowx.sort((e1, e2) => e1[2] - e2[2]);
        const xh = new THREE.Vector3(0, 0, 0);
        let changeInDirection = false;
        rowx.forEach(([vi, d], i) => {
          if (d < 0 && !changeInDirection) {
            const h = vi >= hlen ? newPanels[pi].vertical.height : newPanels[pi].horizontal.height;
            changeInDirection = true;
            xh.set(0, 0, 0);
            xh.sub(AB.clone().multiplyScalar(h + rackBuffer));
          } else if (d > 0) {
            const h = rowx[i - 1][0] >= hlen ? newPanels[pi].vertical.height : newPanels[pi].horizontal.height;
            xh.add(AB.clone().multiplyScalar(h + rackBuffer));
          } else {
            const h = vi >= hlen ? newPanels[pi].vertical.height : newPanels[pi].horizontal.height;
            xh.add(AB.clone().multiplyScalar((h + rackBuffer) * (i == 0 ? 0 : -1)));
          }
          if (vi >= hlen) {
            newPanels[pi].vertical.points[vi - hlen] = [x + xh.x, y + xh.y];
          } else {
            newPanels[pi].horizontal.points[vi] = [x + xh.x, y + xh.y];
          }
        });
      }
    });
    setPanelPlacement(newPanels);
  }, [panelEaves, panelData, panels, readonly, selectedObjects.panels, setPanelPlacement]);

  const roofTransformations = useMemo(() => {
    return pitches.map((pitch, pi) => {
      const tilt = panels[pi]?.tilt ?? 0;
      const isRoof = verticesType[pi] === 'roof';
      const transformation = getRoofTransformationInfo(eaves[pi], eaveHeights[pi], isRoof, pitch, azimuths[pi], tilt);
      return transformation;
    });
  }, [eaves, eaveHeights, pitches, azimuths, panels, verticesType]);

  const [setbacksData, setbacks3dData] = useMemo(() => {
    console.log('🔄 usePanelOperation - setbacksData useMemo recalculating:', {
      verticesCount: vertices?.length,
      verticesTypeCount: verticesType?.length
    });
    const setbacks = vertices.filter((poly) => poly !== null).map((poly, index) => getSetbacks(poly, index) ?? setback);
    console.log('🎯 usePanelOperation - Setbacks calculated:', setbacks);
    const setbacks2D = getSetbackData(vertices, verticesType, setbacks);
    const setbacks3D = setbacks2D.map((setback, index) => {
      const [tr, trx] = [roofTransformations[index][3], roofTransformations[index][4]];
      const setback3D = setback.map((s) => {
        return s.map((v) => {
          const vt = v.clone();
          vt.applyMatrix4(tr);
          if (trx) vt.applyMatrix4(trx);
          return vt;
        });
      });
      return setback3D;
    });
    return [setbacks2D, setbacks3D];
  }, [vertices, verticesType, setback, roofTransformations, getSetbacks]);

  useEffect(() => {
    if (!eaves.length) return;
    const newPanels = getPanelData(
      panels,
      segments,
      pitches,
      vertices3D,
      parents,
      panelEaves,
      eaves,
      eaveHeights,
      cx,
      cy,
      scale,
      resolution,
      buffer,
      setbacks3dData
    );
    setPanelData(newPanels);
    setMoveStatus(0);
  }, [
    segments,
    panels,
    pitches,
    panelEaves,
    eaves,
    vertices3D,
    parents,
    eaveHeights,
    cx,
    cy,
    scale,
    resolution,
    buffer,
    setbacks3dData
  ]);

  return {
    moveStatus,
    panelData,
    panelEaves,
    setbacksData,
    setbacks3dData,
    roofTransformations,
    panelPlacement: protectMethod(panelPlacement, { rethrow: true }),
    panelPlacementByIndices: protectMethod(panelPlacementByIndices, {
      rethrow: true
    }),
    deletePanels: protectMethod(deletePanels),
    rotatePanels: protectMethod(rotatePanels),
    movePanels: protectMethod(movePanels),
    insertPanels: protectMethod(insertPanels),
    canInsertPanel: protectMethod(canInsertPanel),
    alignHorizontal: protectMethod(alignHorizontal),
    alignVertical: protectMethod(alignVertical),
    clearPanels,
    setMoveStatus
  };
};

export default usePanelOperation;
